import { useAuth } from "context/AuthContext";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchOfficeStamp } from "services/stampService";

/**
 * Office stamp for a paper filed to court or sent to the police, as in the
 * discipline system. The stamp is loaded from HRMS for the user's admin unit
 * (browsing is offered only when HRMS has none) and the backend prints it on
 * every page of the PDF when sent as stamp_file. Only prosecutors sign, in
 * the charge sheet, so no signature is taken here.
 */
export type CourtMarks = { stamp_file?: File; signature_file?: File };

export const useCourtFilingMarks = (active: boolean) => {
  const { loginData } = useAuth();
  const [stampFile, setStampFile] = useState<File | null>(null);
  const [stampFromOffice, setStampFromOffice] = useState<boolean | null>(null);
  const adminUnit = loginData?.admin_unit ? String(loginData.admin_unit) : "";

  useEffect(() => {
    if (!active || !adminUnit) return;
    let current = true;
    fetchOfficeStamp(adminUnit)
      .then((file) => {
        if (!current) return;
        setStampFile(file);
        setStampFromOffice(true);
      })
      .catch(() => current && setStampFromOffice(false));
    return () => {
      current = false;
    };
  }, [active, adminUnit]);

  const marks: CourtMarks = useMemo(
    () => ({ stamp_file: stampFile || undefined }),
    [stampFile],
  );

  const reset = () => {
    if (!stampFromOffice) setStampFile(null);
  };

  const fields = active ? (
    <CourtFilingMarksFields
      stampFile={stampFile}
      setStampFile={setStampFile}
      stampFromOffice={stampFromOffice}
    />
  ) : null;

  return { marks, fields, reset };
};

type FieldsProps = {
  stampFile: File | null;
  setStampFile: (file: File | null) => void;
  stampFromOffice: boolean | null;
};

const Preview: React.FC<{ file: File; alt: string }> = ({ file, alt }) => {
  const url = useMemo(() => URL.createObjectURL(file), [file]);
  useEffect(() => () => URL.revokeObjectURL(url), [url]);
  return <img src={url} alt={alt} className="h-12 w-20 object-contain" />;
};

export const CourtFilingMarksFields: React.FC<FieldsProps> = ({
  stampFile,
  setStampFile,
  stampFromOffice,
}) => {
  const { t } = useTranslation();
  const rows = [
    {
      key: "stamp",
      label: t("office_stamp"),
      file: stampFile,
      setFile: setStampFile,
      locked: !!stampFromOffice,
      hint: stampFromOffice
        ? t("stamp_from_office")
        : stampFromOffice === false
          ? t("stamp_not_in_hrms")
          : t("stamp_sign_hint"),
    },
  ];

  return (
    <div className="grid gap-3">
      {rows.map((row) => (
        <label
          key={row.key}
          className={`flex items-center gap-3 rounded-lg border border-dashed border-gray-300 p-3 ${
            row.locked ? "" : "cursor-pointer hover:bg-gray-50"
          }`}
        >
          {row.file ? (
            <Preview file={row.file} alt={row.label} />
          ) : (
            <span className="flex h-12 w-20 items-center justify-center rounded bg-gray-100 text-xl">
              {row.key === "stamp" ? "🔏" : "✍️"}
            </span>
          )}
          <span className="flex-1 text-sm">
            <span className="block font-medium text-gray-800">{row.label}</span>
            <span className="block text-xs text-gray-500">
              {row.file && !row.locked ? row.file.name : row.hint}
            </span>
          </span>
          {row.file && !row.locked && (
            <button
              type="button"
              className="text-xs text-red-600 hover:underline"
              onClick={(e) => {
                e.preventDefault();
                row.setFile(null);
              }}
            >
              ✕
            </button>
          )}
          <input
            type="file"
            accept="image/png,image/jpeg,image/gif"
            disabled={row.locked}
            className="hidden"
            onChange={(e) => {
              row.setFile(e.target.files?.[0] || null);
              e.target.value = "";
            }}
          />
        </label>
      ))}
    </div>
  );
};

/**
 * The request body for a filing: unchanged JSON when there is no stamp or
 * signature, otherwise multipart with the images (null values left out).
 */
export const withCourtMarks = (data: Record<string, any>, marks?: CourtMarks) => {
  const files = Object.entries(marks || {}).filter(([, file]) => !!file);
  if (!files.length) return { body: data, config: undefined };
  const form = new FormData();
  Object.entries(data).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    form.append(key, value instanceof Blob ? value : String(value));
  });
  files.forEach(([key, file]) => form.append(key, file as File));
  return {
    body: form,
    config: { headers: { "Content-Type": "multipart/form-data" } },
  };
};
